import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import { Input, Select } from "../../components/Form";
import Breadcrumbs from "../../components/Breadcrumbs";

import { ToastContext } from "../../contexts/toast/toastContext";
import { ToastErrorContext } from "../../contexts/toast/toastErrorContext";
import { SweetAlertContext } from "../../contexts/sweetAlert/alertContext";

import { Form } from "@unform/web";
import { SubmitHandler, FormHandles } from "@unform/core";
import getValidationErrors from "../../helpers/getValidateErrors";
import { getSingleErrorMessage } from "../../helpers/getSingleErrorMessage";
import * as Yup from "yup";

import GlobalFilter from "../../components/DataTable/GlobalFilter";
import PaginationTable from "../../components/DataTable/paginationTable-BuscaExt";
import DataNotFound from "../../components/DataNotFound";
import api from "../../services/api";

import { IFormPrenativo, IListPrenativo } from "../../common/interfaces/prenativo.interfaces";
import { IPaginacao } from "../../common/interfaces/paginacao.interfaces";
import { toast } from "react-toastify";

const Prenativo: React.FC = () => {

  // Refs
  const formRef = useRef<FormHandles>(null);

  //Fields Typed
  const queryString = require('querystring');
  const DataTableTS: any = PaginationTable;

  //Context
  const sweetAlertContext = useContext(SweetAlertContext);
  const toastError = useContext(ToastErrorContext);
  const toastContext = useContext(ToastContext);

  //States
  const [tableData, setTableData] = useState<IListPrenativo[]>([]);
  const [selected, setSelected] = useState<IFormPrenativo>();
  const [unidade, setUnidade] = useState<any>([]);
  const [show, setShow] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [reloadTable, setReloadTable] = useState(false);
  const [novo, setNovo] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [filter, setFilter] = useState('');


  const tipo = [
    { label: "SÓLIDO", value: 'S' },
    { label: "SÓLIDO BRUTO", value: 'SBRUTO' },
    { label: "TUBULAR", value: 'T' },
    { label: "TUBULAR BRUTO", value: 'TBRUTO' },
    { label: "USINADO", value: 'U' },
    { label: "USINADO SÓLIDO", value: 'US' },
  ];

  //Effects
  useEffect(() => {
    (async () => {
      const { data } = await api.get("unidadeItemEmbalagem/select");
      setUnidade(data);
    })();
  }, []);

  //Callbacks
  const fetchData = useCallback(async (paginacao: IPaginacao) => {
    try {
      const { data: { dados, paginas } } = await api.get(`/prenativo/lista?${queryString.stringify(paginacao)}`);

      setPageCount(paginas);
      setTableData(dados);
    } catch (error) {
      toast.error(getSingleErrorMessage(error));
    } finally {
      setTableLoading(false);
    }
  }, []);

  //Handles
  const handleClose = () => setShow(false);

  const handleNew = () => {
    setSelected({} as IFormPrenativo);
    setNovo(true);
    setShow(true);
  };

  const handleSave = () => {
    formRef.current?.submitForm();
  };

  const handleEdit = useCallback(async ({ id }) => {
    const { data } = await api.get(`prenativo/${id}`);

    console.log(data);

    setSelected(data);
    setShow(true);
    setNovo(false);
  }, []);

  const handleDelete = ({ id, descricao }: any) => {
    const title = `Excluir "${descricao}"?`;
    const message = "Esse prenativo será excluído, isso não pode ser desfeito.";
    sweetAlertContext.danger(title, message, async () => {
      try {
        await api.delete(`prenativo/${id}`);

        setTableData((prevState) =>
          prevState.filter(function (item) {
            return item.id !== id;
          })
        );
        sweetAlertContext.close();
      } catch (error) {
        const message = getSingleErrorMessage(error);
        if (message) {
          toastContext.notification(message, "warn");
        } else {
          toastContext.notification(
            "Ocorreu um erro ao realizar essa operação.",
            "error"
          );
        }
      }
    });
  };

  const handleSubmit: SubmitHandler<IFormPrenativo> = async (prenativo) => {
    try {
      formRef.current?.setErrors({});

      const message = "Campo obrigatório";
      const schema = Yup.object().shape({
        codigo: Yup.string().required(message).max(20, 'O código só pode ter no máximo 20 caracteres.'),
        descricao: Yup.string().required(message).max(100, 'A descrição só pode ter no máximo 100 caracteres.'),
        unidade: Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable()
          .required(message),

        tipo: Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable()
          .required(message),
      });

      await schema.validate(prenativo, { abortEarly: false });

      //Passou pela validação
      prenativo.id = prenativo.id != '' ? prenativo.id : undefined;
      await api.post('prenativo', prenativo);

      setShow(false);
      setNovo(false);
      setReloadTable(reload => !reload);
      console.log(prenativo);

      toast.success("A ação foi concluída.");
    } catch (err) {
      toast.error(getSingleErrorMessage(err));      
      formRef.current?.setErrors(getValidationErrors(err));
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Código",
        accessor: "codigo",
        display: "align-middle",
      },
      {
        Header: "Descrição",
        accessor: "descricao",
        display: "align-middle",
      },
      {
        Header: "Unidade",
        accessor: "unidade",
        display: "align-middle",
      },
      {
        Header: "Tipo",
        accessor: "tipo",
        display: "align-middle",
      },
      {
        Header: "Ação",
        accessor: "acao",
        disableSortBy: true,
        display: "align-middle column-table-2b",
        Cell: ({
          row: { original },
        }: {
          row: { original: IListPrenativo };
        }) => (
          <span className="d-none d-sm-inline">
            <Button
              title="Editar"
              className="p-2"
              variant="ghost-dark"
              onClick={() => handleEdit(original)}
            >
              <i className="far fa-edit icon m-0"></i>
            </Button>
            <Button
              variant="ghost-dark"
              onClick={() => handleDelete(original)}
              className="p-2"
            >
              <i className="far fa-trash-alt icon m-0"></i>
            </Button>
          </span>
        ),
      },
    ],
    []
  );

  return (
    <>
      <div className="content">
        <div className="container-xl">
          <div className="page-header">
            <div className="row">
              <Breadcrumbs />
              <span className="col-auto ml-auto">
                <Button
                  variant="white"
                  onClick={() => handleNew()}
                >
                  Novo Prenativo
                </Button>
              </span>
              <div className="col-auto pr-0">
                <GlobalFilter setGlobalFilter={setFilter} />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="card p-0">
              <div className="card-body">
                <div className={'dimmer' + (tableLoading ? ' active' : '')}>
                  <div className="loader"></div>
                  <div className={"dimmer-content" + (tableLoading ? ' medium-box-loader' : '')} >
                    {tableData && (
                      <DataTableTS
                        filter={filter}
                        columns={columns}
                        bordered={false}
                        data={tableData}
                        pageCount={pageCount}
                        fetchData={fetchData}
                        reload={reloadTable}
                        initialState={{
                          sortBy: [
                            {
                              id: "id",
                              desc: false,
                            },
                          ],
                        }}
                      />
                    )}
                    <DataNotFound
                      visible={
                        tableLoading == false ? tableData?.length === 0 : false
                      }
                    ></DataNotFound>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Prenativo */}
      <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            {novo ? "Adicionar Prenativo" : "Editar Prenativo"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form ref={formRef} onSubmit={handleSubmit} initialData={selected}>
            <Input type="hidden" name="id" />
            <div className="form-row mt-3">
              <div className="form-group col">
                <label className="form-label">Código</label>
                <Input
                  type="text"
                  name="codigo"
                  className="form-control"
                  placeholder="Código..."
                />
              </div>
              <div className="form-group col">
                <label className="form-label">Descrição</label>
                <Input
                  type="text"
                  name="descricao"
                  className="form-control"
                  placeholder="Nome do Prenativo..."
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group col">
                <label className="form-label">Unidade</label>
                <Select
                  name="unidade"
                  placeholder="Selecione uma opção..."
                  options={unidade}
                  defaultValue={selected?.unidade}
                ></Select>
              </div>
              <div className="form-group col">
                <label className="form-label">Tipo</label>
                <Select
                  name="tipo"
                  placeholder="Selecione uma opção..."
                  options={tipo}
                  defaultValue={selected?.tipo}
                ></Select>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Fechar
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Prenativo;
